import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import { alpha, Box, Button, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { FormattedMessage } from 'react-intl';
import { FilterChip } from '../atoms/FilterChip';
import { palette } from '../../settings/themes/colors';

const useFilterChipListStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  chipsBox: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    padding: 1,
    border: `1px solid ${alpha(palette.darkGray20, 0.2)}`,
    borderRadius: 4
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    alignItems: 'center',
    flex: '1 1 auto',
    minWidth: 0,
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1)
  },
  measureRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    top: -9999,
    left: 0
  },
  overflowLabel: {
    color: palette.dimGray,
    fontWeight: 500,
    whiteSpace: 'nowrap'
  },
  viewAllButton: {
    height: 34,
    margin: 0,
    borderRadius: 3,
    textTransform: 'none',
    flexShrink: 0,
    padding: '4px 5px',
    minWidth: 'auto',
    gap: 8,
    backgroundColor: palette.captudataBlue,
    color: palette.white,
    border: `1px solid ${alpha(palette.darkGray20, 0.2)}`,
    fontFamily: 'Nunito',
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: '0.15px',
    '&:hover': {
      backgroundColor: palette.peakcock
    }
  },
  viewAllIcon: {
    fontSize: 18,
    color: palette.white
  },
  clearLink: {
    flexShrink: 0,
    color: palette.cerulean,
    textDecoration: 'underline',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    fontSize: '0.8125rem'
  }
}));

const OVERFLOW_LABEL_RESERVED_WIDTH = 40;
const VIEW_ALL_BUTTON_RESERVED_WIDTH = 110;
const VIEW_ALL_RESERVED_WIDTH = OVERFLOW_LABEL_RESERVED_WIDTH + VIEW_ALL_BUTTON_RESERVED_WIDTH;

export const FilterChipList = ({ chips = [], onClearFilters, hasActiveFilters = true }) => {
  const classes = useFilterChipListStyles();
  const containerRef = useRef(null);
  const measureRefs = useRef([]);
  const [visibleCount, setVisibleCount] = useState(chips.length);
  const [isExpanded, setIsExpanded] = useState(false);

  useLayoutEffect(() => {
    measureRefs.current = measureRefs.current.slice(0, chips.length);
  }, [chips.length]);

  useEffect(() => {
    const recalculate = () => {
      const container = containerRef.current;

      if (!container) return;

      const availableWidth = container.offsetWidth - VIEW_ALL_RESERVED_WIDTH;
      let usedWidth = 0;
      let count = 0;

      for (let i = 0; i < chips.length; i += 1) {
        const chipWidth = measureRefs.current[i] ? measureRefs.current[i].offsetWidth + 8 : 0;

        if (usedWidth + chipWidth > availableWidth && count > 0) break;
        usedWidth += chipWidth;
        count += 1;
      }

      setVisibleCount(count);
    };

    recalculate();

    const resizeObserver = new ResizeObserver(recalculate);

    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [chips]);

  const chipsSignature = chips.map(chip => chip.key).join('|');

  useEffect(() => {
    setIsExpanded(false);
  }, [chipsSignature]);

  if (chips.length === 0) return null;

  const overflowCount = chips.length - visibleCount;
  const displayedChips = isExpanded || overflowCount <= 0 ? chips : chips.slice(0, visibleCount);

  return (
    <Box className={classes.wrapper}>
      <Box ref={containerRef} className={classes.chipsBox}>
        <Box className={classes.measureRow} aria-hidden="true">
          {chips.map((chip, index) => (
            <Box
              key={chip.key}
              ref={element => {
                measureRefs.current[index] = element;
              }}
            >
              <FilterChip label={chip.label} />
            </Box>
          ))}
        </Box>

        <Box className={classes.chipsRow}>
          {displayedChips.map(({ key, label, onRemove }) => (
            <FilterChip key={key} label={label} onDelete={onRemove} />
          ))}

          {!isExpanded && overflowCount > 0 && <span className={classes.overflowLabel}>{`+${overflowCount}`}</span>}
        </Box>

        {!isExpanded && overflowCount > 0 && (
          <Button size="small" className={classes.viewAllButton} onClick={() => setIsExpanded(true)}>
            <ExpandMoreIcon className={classes.viewAllIcon} />
            <FormattedMessage id="common.viewAllFilters" />
          </Button>
        )}
      </Box>

      {onClearFilters && (
        <Button size="small" className={classes.clearLink} onClick={onClearFilters} disabled={!hasActiveFilters}>
          <FormattedMessage id="common.clearFilters" />
        </Button>
      )}
    </Box>
  );
};

FilterChipList.propTypes = {
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.node]).isRequired,
      onRemove: PropTypes.func
    })
  ),
  onClearFilters: PropTypes.func,
  hasActiveFilters: PropTypes.bool
};
